import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

/**
 * Fetches a user's public profile by username.
 * Two-step lookup: usernames/{lowercase} → uid → users/{uid}
 */
export function usePublicProfile(username) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!username) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    try {
      // Step 1: Look up uid from usernames collection
      const usernameDoc = await getDoc(doc(db, 'usernames', username.toLowerCase()));
      if (!usernameDoc.exists()) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const { uid } = usernameDoc.data();

      // Step 2: Fetch user profile
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (!userDoc.exists()) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const data = userDoc.data();
      // Only expose safe fields — no email, phone
      setProfile({
        uid,
        username: data.username,
        displayName: data.displayName,
        activeBadge: data.activeBadge || 'newbie',
        badges: data.badges || ['newbie'],
        currentStreak: data.currentStreak || 0,
        longestStreak: data.longestStreak || 0,
        totalPicks: data.totalPicks || 0,
        totalCorrect: data.totalCorrect || 0,
        createdAt: data.createdAt,
      });
    } catch (err) {
      console.error('Error fetching public profile:', err);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return { profile, loading, notFound };
}
